import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { X, Loader2, Minus, Plus } from 'lucide-react';
import { useAccountStore } from '../../stores/accountStore';
import { useFeeFor } from '@/services/hydraFees';
import { perContractFeeCents } from '@/lib/pricing/fees';
import type { GameRow, VenueBook } from './types';
import { americanOdds, bestPriceCentsForOrderSide, outcomeSideLabel } from './tradeModalBook';

export interface TradeModalData {
  game: GameRow;
  teamSide: 'away' | 'home';
  venue: 'kalshi' | 'polymarket';
  prefillPrice?: number;
  initialOrderSide?: 'yes' | 'no';
  postOnly: boolean;
}

interface TradeModalProps {
  data: TradeModalData;
  onClose: () => void;
  /** Optional: fired right after a successful order placement so the parent
   *  can refresh per-user activity (POS column chips, etc.) without waiting
   *  for the next 5 s poll. */
  onOrderPlaced?: () => void;
}

type ExpiryMode = 'preset' | 'custom' | 'gtc';

function buildExpiryIso(dateStr: string, timeStr: string): string | null {
  const [month, day, year] = dateStr.split('/').map(Number);
  if (!month || !day || !year) return null;

  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();

  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;

  const dt = new Date(Date.UTC(year, month - 1, day, hours + 7, minutes));
  return dt.toISOString();
}

function todayDateStr(): string {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

function defaultPresetTime(): string {
  const now = new Date();
  const ptHours = (now.getUTCHours() - 7 + 24) % 24;
  const ptMinutes = Math.ceil(now.getUTCMinutes() / 10) * 10;
  const h = ptMinutes >= 60 ? ptHours + 1 : ptHours;
  const m = ptMinutes >= 60 ? 0 : ptMinutes;
  const hour12 = h % 12 || 12;
  const period = h >= 12 ? 'PM' : 'AM';
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function StepperInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  placeholder,
  suffix,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  suffix?: string;
  autoFocus?: boolean;
}) {
  const numVal = parseInt(value, 10);
  const canDec = !isNaN(numVal) && (min === undefined || numVal - step >= min);
  const canInc = !isNaN(numVal) && (max === undefined || numVal + step <= max);

  return (
    <div className="bg-muted border-border flex items-center rounded-lg border">
      <button
        type="button"
        onClick={() => canDec && onChange(String(numVal - step))}
        disabled={!canDec}
        className="text-muted-foreground hover:text-foreground flex h-10 w-10 items-center justify-center transition-colors disabled:opacity-30"
      >
        <Minus size={14} />
      </button>
      <div className="relative flex-1">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className="text-foreground placeholder-muted-foreground w-full bg-transparent py-2 text-center font-mono text-lg focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        {suffix && value && (
          <span className="text-muted-foreground pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm">{suffix}</span>
        )}
      </div>
      <button
        type="button"
        onClick={() => {
          if (value === '' || isNaN(numVal)) {
            onChange(String(min ?? step));
          } else if (canInc) {
            onChange(String(numVal + step));
          }
        }}
        disabled={!isNaN(numVal) && !canInc}
        className="text-muted-foreground hover:text-foreground flex h-10 w-10 items-center justify-center transition-colors disabled:opacity-30"
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

// ── Time preset options ───────────────────────────────────────────

const TIME_OPTIONS = (() => {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 10) {
      const hour12 = h % 12 || 12;
      const period = h >= 12 ? 'PM' : 'AM';
      opts.push(`${hour12}:${String(m).padStart(2, '0')} ${period}`);
    }
  }
  return opts;
})();

export function TradeModal({ data, onClose, onOrderPlaced }: TradeModalProps) {
  const status = useAccountStore((s) => s.status);
  const placeOrder = useAccountStore((s) => s.placeOrder);

  const { game, teamSide, venue, postOnly } = data;
  const isKalshiVenue = venue === 'kalshi';
  const teamCode = teamSide === 'away' ? game.awayCode : game.homeCode;
  const venueLabel = venue === 'kalshi' ? 'KALSHI' : 'POLY';

  const book: VenueBook | undefined = teamSide === 'away'
    ? (venue === 'kalshi' ? game.kalshi?.away : game.polymarket?.away)
    : (venue === 'kalshi' ? game.kalshi?.home : game.polymarket?.home);

  const ticker = book?.marketTicker ?? '';
  const initialSide = data.initialOrderSide ?? 'no';
  const bestForInitial =
    data.prefillPrice ?? bestPriceCentsForOrderSide(book, initialSide);
  const bestYesHint = bestPriceCentsForOrderSide(book, 'yes');
  const bestNoHint = bestPriceCentsForOrderSide(book, 'no');

  const [orderSide, setOrderSide] = useState<'yes' | 'no'>(initialSide);
  const [price, setPrice] = useState(
    () => (bestForInitial !== undefined ? String(bestForInitial) : ''),
  );
  const [qty, setQty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const autoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [expiryMode, setExpiryMode] = useState<ExpiryMode>('gtc');
  const [expiryDate, setExpiryDate] = useState(todayDateStr);
  const [expiryTime, setExpiryTime] = useState(defaultPresetTime);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    return () => {
      if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    };
  }, []);

  const applyOrderSide = useCallback(
    (next: 'yes' | 'no') => {
      setOrderSide(next);
      setResult(null);
      const hint = bestPriceCentsForOrderSide(book, next);
      if (hint !== undefined) setPrice(String(hint));
    },
    [book],
  );

  const priceCents = parseInt(price, 10) || 0;
  const count = parseInt(qty, 10) || 0;
  const totalCost = (priceCents * count) / 100;
  const maxPayout = count;

  // Per-series fee schedule from hydra. `postOnly` is the canonical
  // maker/taker signal in this modal — the badge above already labels it.
  const feeEntry = useFeeFor(ticker);
  const isMaker = postOnly;
  const feeRate = !feeEntry
    ? null
    : feeEntry.feeType === 'flat'
      ? null
      : isMaker
        ? (feeEntry.feeMakerRate ?? 0)
        : (feeEntry.feeTakerRate ?? 0);
  const feePerContractCents =
    feeRate === null || priceCents === 0 ? null : perContractFeeCents(priceCents, feeRate);
  const fee =
    feePerContractCents === null || count === 0
      ? null
      : (feePerContractCents / 100) * count;
  const feeLabel = feeRate === null ? '—' : `$${(fee ?? 0).toFixed(3)}`;
  const effectivePriceCents =
    feePerContractCents === null ? null : priceCents + feePerContractCents;
  const netPayout = fee === null ? null : maxPayout - fee;
  const netPayoutLabel = netPayout === null ? '—' : `$${netPayout.toFixed(2)}`;
  const effectiveOddsLabel =
    effectivePriceCents === null ? '—' : americanOdds(effectivePriceCents);
  const maxProfit = netPayout === null ? null : netPayout - totalCost;

  const expiryIso = useMemo(() => {
    if (expiryMode === 'gtc') return undefined;
    return buildExpiryIso(expiryDate, expiryTime) ?? undefined;
  }, [expiryMode, expiryDate, expiryTime]);

  const handleSubmit = useCallback(async () => {
    const p = parseInt(price, 10);
    const c = parseInt(qty, 10);

    if (!ticker) { setResult({ ok: false, msg: 'No market ticker found' }); return; }
    if (isNaN(p) || p < 1 || p > 99) { setResult({ ok: false, msg: 'Price must be 1-99\u00A2' }); return; }
    if (isNaN(c) || c < 1) { setResult({ ok: false, msg: 'Qty must be at least 1' }); return; }

    setSubmitting(true);
    setResult(null);
    try {
      await placeOrder({
        ticker,
        side: orderSide,
        action: 'buy',
        type: 'limit',
        count: c,
        price: p,
        ...(isKalshiVenue && postOnly ? { postOnly: true } : {}),
        ...(expiryIso ? { expirationTime: expiryIso } : {}),
      });
      setResult({ ok: true, msg: `Order placed: ${c} ${orderSide.toUpperCase()} @ ${p}\u00A2` });
      setQty('');
      onOrderPlaced?.();
      autoCloseRef.current = setTimeout(() => onClose(), 1500);
    } catch (err) {
      setResult({ ok: false, msg: err instanceof Error ? err.message : 'Order failed' });
    } finally {
      setSubmitting(false);
    }
  }, [ticker, orderSide, price, qty, placeOrder, expiryIso, onClose, onOrderPlaced, isKalshiVenue, postOnly]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-card border-border w-[420px] rounded-lg border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-border flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-foreground text-base font-bold tracking-wide">PLACE ORDER</h2>
          <button onClick={onClose} className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-5 py-4">
          {status !== 'connected' ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Connect your Kalshi account in Settings to trade
            </p>
          ) : !isKalshiVenue ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Polymarket order entry is not wired into the multi-tenant relay yet.
            </p>
          ) : !ticker ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No {venue === 'kalshi' ? 'Kalshi' : 'Polymarket'} market available
            </p>
          ) : (
            <>
              {/* Team + Badges */}
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground text-lg font-bold">{teamCode}</span>
                  <span className="rounded bg-green-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">WIN</span>
                  <span className="rounded bg-blue-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">{venueLabel}</span>
                  <span className="rounded bg-purple-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {postOnly ? 'MAKER' : 'TAKER'}
                  </span>
                  {game.kalshiViaSeries && venue === 'kalshi' && (
                    <span className="rounded bg-amber-600/80 px-1.5 py-0.5 text-[10px] font-bold text-white">SERIES</span>
                  )}
                </div>
                <p className="text-muted-foreground mt-1 font-mono text-xs">
                  {game.awayCode} @ {game.homeCode} &middot; {game.date} &middot; {ticker.length > 40 ? `${ticker.slice(0, 20)}...${ticker.slice(-18)}` : ticker}
                </p>
                {game.kalshiViaSeries && venue === 'kalshi' && (
                  <p className="mt-2 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] leading-snug text-amber-300">
                    Kalshi has no per-day market for this clincher — this trade settles on the
                    {' '}<span className="font-semibold">series outcome</span>, not on tonight's game.
                    A NO bet here is &ldquo;{teamCode} doesn't win the series,&rdquo; not &ldquo;{teamCode} loses tonight.&rdquo;
                  </p>
                )}
              </div>

              {/* Side */}
              <div className="border-border rounded-lg border px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Side</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => applyOrderSide('yes')}
                      className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                        orderSide === 'yes'
                          ? 'bg-green-600 text-white'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      YES
                    </button>
                    <button
                      type="button"
                      onClick={() => applyOrderSide('no')}
                      className={`rounded px-3 py-1 text-xs font-bold transition-colors ${
                        orderSide === 'no'
                          ? 'bg-red-600 text-white'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      NO
                    </button>
                    <span className="text-foreground text-sm">
                      {outcomeSideLabel(game, teamSide, orderSide)}
                    </span>
                  </div>
                </div>

                {/* Limit Price */}
                <div className="mt-3">
                  <label className="text-muted-foreground mb-1.5 block text-xs">Limit Price</label>
                  <StepperInput
                    value={price}
                    onChange={(v) => { setPrice(v); setResult(null); }}
                    min={1}
                    max={99}
                    placeholder={
                      (orderSide === 'yes' ? bestYesHint : bestNoHint) !== undefined
                        ? String(orderSide === 'yes' ? bestYesHint : bestNoHint)
                        : '50'
                    }
                    suffix={'\u00A2'}
                    autoFocus
                  />
                </div>
              </div>

              {/* Contracts */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-muted-foreground text-xs">Contracts</label>
                </div>
                <StepperInput
                  value={qty}
                  onChange={(v) => { setQty(v); setResult(null); }}
                  min={1}
                  step={1}
                  placeholder="0"
                />
                {count > 0 && priceCents > 0 && (
                  <p className="text-muted-foreground mt-1.5 text-center text-xs">
                    ${totalCost.toFixed(2)} total cost
                  </p>
                )}
              </div>

              {/* Summary box */}
              {count > 0 && priceCents > 0 && (
                <div className="border-border space-y-1.5 rounded-lg border px-4 py-3">
                  <div className="flex items-center justify-between text-sm">
                    <span
                      className="text-muted-foreground"
                      title={
                        isMaker
                          ? 'Maker fee — applied only when the resting order fills'
                          : 'Taker fee — applied immediately on fill'
                      }
                    >
                      Total Cost (fee)
                    </span>
                    <span className="text-foreground font-mono font-medium">
                      ${totalCost.toFixed(2)}{' '}
                      <span className="text-muted-foreground">({feeLabel})</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground" title="Payout minus fee">
                      Max Payout
                    </span>
                    <span className="text-foreground font-mono font-medium inline-flex items-center gap-1.5">
                      <span>{netPayoutLabel}</span>
                      <span className="h-3 w-px self-center bg-current opacity-30" />
                      <span className="opacity-70">{effectiveOddsLabel}</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Max Profit</span>
                    <span
                      className={`font-mono font-medium ${
                        maxProfit === null
                          ? 'text-muted-foreground'
                          : maxProfit >= 0
                            ? 'text-green-400'
                            : 'text-red-400'
                      }`}
                    >
                      {maxProfit === null
                        ? '—'
                        : `${maxProfit >= 0 ? '+' : ''}${maxProfit.toFixed(2)}`}
                    </span>
                  </div>
                </div>
              )}

              {/* Expiry */}
              <div>
                <label className="text-muted-foreground mb-1.5 block text-xs">Expiry</label>
                <div className="mb-2 flex gap-1.5">
                  {(['preset', 'custom', 'gtc'] as ExpiryMode[]).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setExpiryMode(mode)}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        expiryMode === mode
                          ? 'bg-blue-600 text-white'
                          : 'bg-muted text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {mode === 'preset' ? 'Preset' : mode === 'custom' ? 'Custom' : 'GTC'}
                    </button>
                  ))}
                </div>
                {expiryMode !== 'gtc' && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      placeholder="MM/DD/YYYY"
                      className="bg-muted border-border text-foreground placeholder-muted-foreground flex-1 rounded-lg border px-3 py-2 font-mono text-sm focus:border-blue-500 focus:outline-none"
                    />
                    <select
                      value={expiryTime}
                      onChange={(e) => setExpiryTime(e.target.value)}
                      className="bg-muted border-border text-foreground flex-1 rounded-lg border px-3 py-2 font-mono text-sm focus:border-blue-500 focus:outline-none"
                    >
                      {TIME_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t} PT</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Result message */}
              {result && (
                <div className={`rounded-lg px-4 py-2.5 text-sm ${result.ok ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                  {result.msg}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {status === 'connected' && ticker && isKalshiVenue && (
          <div className="border-border flex items-center justify-end gap-3 border-t px-5 py-3">
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground rounded-md px-4 py-2 text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !price || !qty}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? 'Placing...' : 'Place Order'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
